import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, FileText, ChevronRight } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';

export default function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [ageStr, setAgeStr] = useState('');
  const [allergies, setAllergies] = useState('');
  const [conditions, setConditions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name || '');
    setAgeStr(user.age != null ? String(user.age) : '');
    setAllergies(user.allergies || '');
    setConditions(user.conditions || '');
  }, [user]);

  const onSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSavedFlash(false);
    let age = null;
    if (ageStr.trim()) {
      const n = parseInt(ageStr.trim(), 10);
      if (Number.isNaN(n) || n < 0 || n > 130) {
        setError('Age must be a number between 0 and 130.');
        return;
      }
      age = n;
    }
    setSubmitting(true);
    try {
      await updateProfile({
        name: name.trim(),
        age,
        allergies: allergies.trim() || null,
        conditions: conditions.trim() || null,
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) {
      setError(e.message || 'Could not save profile.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-12" data-testid="profile-screen">
      <h1 className="text-3xl font-extrabold mb-1" style={{ color: '#5B2D1F' }}>Your profile</h1>
      <p className="text-base mb-6" style={{ color: '#4B6B79' }}>Help us tailor explanations to you. Everything stays on your account.</p>

      {error && (
        <div data-testid="profile-error" className="p-4 rounded-xl border mb-4" style={{ backgroundColor: '#FCE7EA', borderColor: '#B0303A' }}>
          <p className="font-semibold" style={{ color: '#B0303A' }}>{error}</p>
        </div>
      )}
      {savedFlash && (
        <div data-testid="profile-saved-flash" className="p-4 rounded-xl border mb-4" style={{ backgroundColor: '#E4F4EB', borderColor: '#1F7A4A' }}>
          <p className="font-bold" style={{ color: '#1F7A4A' }}>Saved.</p>
        </div>
      )}

      <form onSubmit={onSave} className="space-y-4">
        <div>
          <label htmlFor="profile-name" className="block text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Name</label>
          <input id="profile-name" data-testid="profile-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 rounded-xl border text-base outline-none focus:ring-2" style={{ borderColor: '#D7E2E8', backgroundColor: '#FFFFFF' }} />
        </div>
        <div>
          <label htmlFor="profile-age" className="block text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Age</label>
          <input id="profile-age" data-testid="profile-age-input" type="text" inputMode="numeric" value={ageStr} onChange={(e) => setAgeStr(e.target.value)} placeholder="Optional" className="w-full px-4 py-3 rounded-xl border text-base outline-none focus:ring-2" style={{ borderColor: '#D7E2E8', backgroundColor: '#FFFFFF' }} />
        </div>
        <div>
          <label htmlFor="profile-allergies" className="block text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Allergies</label>
          <textarea id="profile-allergies" data-testid="profile-allergies-input" value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="e.g. penicillin, sulfa drugs" rows={2} className="w-full px-4 py-3 rounded-xl border text-base outline-none focus:ring-2 resize-none" style={{ borderColor: '#D7E2E8', backgroundColor: '#FFFFFF' }} />
          <p className="text-xs mt-1" style={{ color: '#4B6B79' }}>Comma-separated. Used to flag risky combinations later.</p>
        </div>
        <div>
          <label htmlFor="profile-conditions" className="block text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Existing conditions</label>
          <textarea id="profile-conditions" data-testid="profile-conditions-input" value={conditions} onChange={(e) => setConditions(e.target.value)} placeholder="e.g. hypertension, type 2 diabetes" rows={2} className="w-full px-4 py-3 rounded-xl border text-base outline-none focus:ring-2 resize-none" style={{ borderColor: '#D7E2E8', backgroundColor: '#FFFFFF' }} />
        </div>
        <button type="submit" disabled={submitting} data-testid="profile-save-btn" className="w-full py-4 rounded-xl text-lg font-extrabold text-white transition-opacity hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#D77A52' }}>
          {submitting ? 'Saving…' : 'Save changes'}
        </button>
      </form>

      {/* Links to History & Summary */}
      <div className="mt-8">
        <p className="text-xs font-extrabold uppercase tracking-widest mb-3" style={{ color: '#4B6B79' }}>History & sharing</p>
        <button
          onClick={() => navigate('/history')}
          data-testid="profile-history-link"
          className="w-full flex items-center gap-4 p-4 rounded-2xl border mb-2 text-left transition-opacity hover:opacity-90"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}
        >
          <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: '#EEF4F7' }}>
            <Clock className="h-5 w-5" style={{ color: '#5B2D1F' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-extrabold" style={{ color: '#0F2A35' }}>View activity history</p>
            <p className="text-sm mt-0.5" style={{ color: '#4B6B79' }}>Medications, doses, and interaction checks in one timeline.</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0" style={{ color: '#4B6B79' }} />
        </button>
        <button
          onClick={() => navigate('/summary')}
          data-testid="profile-summary-link"
          className="w-full flex items-center gap-4 p-4 rounded-2xl border mb-2 text-left transition-opacity hover:opacity-90"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}
        >
          <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: '#EEF4F7' }}>
            <FileText className="h-5 w-5" style={{ color: '#5B2D1F' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-extrabold" style={{ color: '#0F2A35' }}>Generate a summary for your doctor</p>
            <p className="text-sm mt-0.5" style={{ color: '#4B6B79' }}>Current meds, interactions, and adherence — ready to share.</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0" style={{ color: '#4B6B79' }} />
        </button>
      </div>

      {/* Read-only email */}
      <div className="mt-6 rounded-xl p-4" style={{ backgroundColor: '#EEF4F7' }}>
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#4B6B79' }}>Email</p>
        <p className="text-base mt-0.5" style={{ color: '#0F2A35' }}>{user?.email}</p>
        <p className="text-xs mt-1" style={{ color: '#4B6B79' }}>Your email is used to sign in and cannot be changed yet.</p>
      </div>
    </div>
  );
}
